import type { AccountAddress, AccountAddressInput } from '@akademi/site-builder-runtime';

/**
 * An address as a form holds it: every field a string, so an input is never
 * switched between controlled and uncontrolled by a null.
 */
export interface AddressDraft {
  label: string;
  recipientName: string;
  company: string;
  line1: string;
  line2: string;
  locality: string;
  region: string;
  postcode: string;
  countryCode: string;
  phone: string;
  makeDefault: boolean;
}

export function emptyDraft(countryCode: string, recipientName = ''): AddressDraft {
  return {
    label: '',
    recipientName,
    company: '',
    line1: '',
    line2: '',
    locality: '',
    region: '',
    postcode: '',
    countryCode,
    phone: '',
    makeDefault: false,
  };
}

export function draftFrom(address: AccountAddress): AddressDraft {
  return {
    label: address.label ?? '',
    recipientName: address.recipientName ?? '',
    company: address.company ?? '',
    line1: address.line1 ?? '',
    line2: address.line2 ?? '',
    locality: address.locality ?? '',
    region: address.region ?? '',
    postcode: address.postcode ?? '',
    countryCode: address.countryCode ?? '',
    phone: address.phone ?? '',
    makeDefault: address.defaultAddress,
  };
}

/** Trimmed, blanks left out, the country upper-cased — what the server stores anyway. */
export function inputFrom(draft: AddressDraft): AccountAddressInput {
  const optional = (value: string) => value.trim() || undefined;
  return {
    label: optional(draft.label),
    recipientName: draft.recipientName.trim(),
    company: optional(draft.company),
    line1: draft.line1.trim(),
    line2: optional(draft.line2),
    locality: optional(draft.locality),
    region: optional(draft.region),
    postcode: optional(draft.postcode),
    countryCode: draft.countryCode.trim().toUpperCase(),
    phone: optional(draft.phone),
    ...(draft.makeDefault ? { makeDefault: true } : {}),
  };
}

type Printable = Pick<
  AccountAddress,
  'recipientName' | 'company' | 'line1' | 'line2' | 'locality' | 'region' | 'postcode' | 'countryCode'
>;

/** The address as it would be written on an envelope, one line each, blanks dropped. */
export function addressLines(address: Printable): string[] {
  const town = [address.locality, address.region].filter(Boolean).join(', ');
  return [
    address.recipientName,
    address.company,
    address.line1,
    address.line2,
    town,
    address.postcode,
    address.countryCode,
  ]
    .map((line) => (line ?? '').trim())
    .filter(Boolean);
}

/** One line for a picker: "Home — 1 High Street, Leeds, LS2 7EW". */
export function addressSummary(address: AccountAddress): string {
  const place = [address.line1, address.locality, address.postcode]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(', ');
  const label = (address.label ?? '').trim();
  return label ? `${label} — ${place}` : place;
}
